import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getWorkflowRunApi, getWorkflowRunStepsApi, listWorkflowRunsApi, type WorkflowRunStatus, type WorkflowRunSummary } from '@/features/system/orchestrator/api/workflowRunsApi';
import { bff } from '@/lib/http/bff';
import { timeAgo } from '@/utils/date';
import { isRunningStatus, vumeterBorderClass, vumeterTextClass } from '@/utils/runStatusUi';
import DarkDataTable from '@/shared/components/ui/DarkDataTable';
import WorkflowRunOutcomesPanel from '../outcomes/WorkflowRunOutcomesPanel';

const LIST_QUERY_KEY = ['developer', 'workflowRuns', 'list'] as const;

function pickRunId(r: WorkflowRunSummary): string {
  return String((r as any).ID ?? (r as any).id ?? '');
}

function pickWorkflowKey(r: WorkflowRunSummary): string {
  return String((r as any).WorkflowKey ?? (r as any).workflow_key ?? '');
}

function pickStatus(r: WorkflowRunSummary): WorkflowRunStatus {
  return String((r as any).Status ?? (r as any).status ?? '');
}

function pickDetailStatus(detail: any): WorkflowRunStatus {
  return String(detail?.status ?? detail?.Status ?? '');
}

function isTerminalStatus(status: any): boolean {
  const s = String(status ?? '').toUpperCase();
  return s === 'SUCCEEDED' || s === 'FAILED' || s === 'ERROR' || s === 'DONE';
}

function deriveStatusFromSteps(stepsRaw: any): WorkflowRunStatus {
  const steps: any[] = Array.isArray(stepsRaw?.items) ? stepsRaw.items : (Array.isArray(stepsRaw) ? stepsRaw : []);

  let total = 0;
  let succeeded = 0;
  let failed = 0;
  let running = 0;
  let ready = 0;

  for (const s of steps) {
    total++;
    const st = String(s?.status ?? s?.Status ?? '').trim().toUpperCase();
    const lastError =
      s?.last_error ?? s?.LastError ?? s?.error_message ?? s?.ErrorMessage;
    const hasErr = Boolean(String(lastError ?? '').trim());

    if (st === 'SUCCEEDED') succeeded++;
    else if (st === 'FAILED' || st === 'ERROR' || hasErr) failed++;
    else if (st === 'RUNNING') running++;
    else if (st === 'READY') ready++;
  }

  if (total > 0) {
    if (failed > 0) return 'ERROR';
    if (running > 0) return 'RUNNING';
    if (succeeded === total) return 'SUCCEEDED';
    if (ready > 0) return 'READY';
    return 'PENDING';
  }
  return '';
}

function pickCreatedAt(r: WorkflowRunSummary): string {
  return String((r as any).CreatedAt ?? (r as any).created_at ?? '');
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&');
}

function buildWildcardMatcher(raw: string): ((value: string) => boolean) | null {
  const q = String(raw ?? '').trim();
  if (!q) return null;

  const hasWildcard = q.includes('*');
  if (!hasWildcard) {
    const lower = q.toLowerCase();
    return (value: string) => String(value ?? '').toLowerCase().includes(lower);
  }

  const parts = q.split('*').map((p) => escapeRegExp(p));
  const pattern = '^' + parts.join('.*') + '$';
  let re: RegExp;
  try {
    re = new RegExp(pattern, 'i');
  } catch {
    return null;
  }
  return (value: string) => re.test(String(value ?? ''));
}

function statusClass(status: string) {
  const s = String(status).toUpperCase();
  if (s === 'SUCCEEDED') return 'text-emerald-300 bg-emerald-950/30 border-emerald-900/40';
  if (s === 'FAILED' || s === 'ERROR') return 'text-rose-300 bg-rose-950/30 border-rose-900/40';
  if (isRunningStatus(s)) return 'text-neutral-200 pac-glass border-neutral-700/50';
  if (s === 'PENDING') return 'text-neutral-300 pac-glass border-neutral-800';
  return 'text-neutral-300 pac-glass border-neutral-800';
}

function RunStatusPill({ runId, summaryStatus, enabled }: { runId: string; summaryStatus: WorkflowRunStatus; enabled: boolean }) {
  const runEnabled = enabled && Boolean(runId);
  const { data } = useQuery({
    queryKey: ['developer', 'workflowRuns', 'get', runId] as const,
    queryFn: () => getWorkflowRunApi(runId),
    staleTime: 10_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    enabled: runEnabled,
  });

  const summary = String(summaryStatus ?? '');
  const detail = pickDetailStatus((data as any)?.run);

  const shouldDeriveFromSteps = (() => {
    const s = String(summary || detail || '').toUpperCase();
    return runEnabled && !isTerminalStatus(s) && (s === 'PENDING' || s === 'READY' || s === 'RUNNING' || !s);
  })();

  const { data: stepsData } = useQuery({
    queryKey: ['developer', 'workflowRuns', 'steps', runId] as const,
    queryFn: () => getWorkflowRunStepsApi(runId),
    staleTime: 5_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    enabled: shouldDeriveFromSteps,
  });

  const derived = shouldDeriveFromSteps ? deriveStatusFromSteps(stepsData) : '';

  // Prefer terminal status from either source; this avoids showing PENDING
  // when the live/run-detail endpoint already knows the run failed/succeeded.
  const chosen =
    (isTerminalStatus(summary) ? summary : '')
    || (isTerminalStatus(detail) ? detail : '')
    || (isTerminalStatus(derived) ? derived : '')
    || summary
    || derived
    || detail
    || '—';

  const running = isRunningStatus(chosen);

  return (
    <span
      className={
        'relative overflow-hidden text-[11px] px-1.5 py-0.5 rounded border '
        + statusClass(String(chosen))
        + (running ? vumeterBorderClass : '')
      }
    >
      <span className={running ? vumeterTextClass : ''}>{String(chosen)}</span>
    </span>
  );
}

function compactJsonPreview(value: any, maxLen = 180): { preview: string; full: string } {
  const full = (() => {
    try {
      // Compact one-line JSON for dense tables.
      return JSON.stringify(value);
    } catch {
      try { return String(value); } catch { return ''; }
    }
  })();

  const s = full || '';
  if (s.length <= maxLen) return { preview: s, full: s };
  return { preview: `${s.slice(0, maxLen - 1)}…`, full: s };
}

function formatDateShort(value: string) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  } catch {
    return value;
  }
}

function RunInputs({ runId, enabled }: { runId: string; enabled: boolean }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['developer', 'workflowRuns', 'get', runId] as const,
    queryFn: () => getWorkflowRunApi(runId),
    staleTime: 10_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    enabled: enabled && Boolean(runId),
  });

  if (!runId) return <span className="text-neutral-600">—</span>;
  if (!enabled) return <span className="text-neutral-600">—</span>;
  if (isLoading) return <span className="text-neutral-600">…</span>;
  if (error) return <span className="text-neutral-600">—</span>;

  const run: any = (data as any)?.run ?? {};
  const inputs = run.inputs ?? run.Inputs ?? {};

  const { preview, full } = compactJsonPreview(inputs);
  if (!preview || preview === '{}' || preview === 'null') return <span className="text-neutral-600">—</span>;

  return (
    <span
      className="text-xs text-neutral-300 font-mono whitespace-nowrap overflow-hidden text-ellipsis block"
      title={full}
    >
      {preview}
    </span>
  );
}

export default function WorkflowActivityLogPanel() {
  // Force a re-render every second so the "Time" column (timeAgo) stays live.
  const [, bumpTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => bumpTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const [selectedRunId, setSelectedRunId] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [searchAll, setSearchAll] = useState<boolean>(false);

  const { data, isLoading, error } = useQuery({
    queryKey: LIST_QUERY_KEY,
    queryFn: () => listWorkflowRunsApi({ limit: 50 }),
    refetchInterval: 10_000,
    refetchIntervalInBackground: true,
  });

  const trimmedSearch = searchText.trim();
  const matcher = useMemo(() => buildWildcardMatcher(trimmedSearch), [trimmedSearch]);
  const hasSearchQuery = Boolean(trimmedSearch);

  const allRunsQuery = useQuery({
    queryKey: ['developer', 'workflowRuns', 'listAll', trimmedSearch] as const,
    queryFn: async () => {
      const items: WorkflowRunSummary[] = [];

      const pageLimit = 200;
      const maxItems = 2000;
      const maxPages = 25;

      let cursor: number | undefined;
      for (let page = 0; page < maxPages; page++) {
        const res = await listWorkflowRunsApi({ limit: pageLimit, cursor });
        const batch = Array.isArray((res as any)?.items) ? (res as any).items : [];
        for (const r of batch) {
          items.push(r);
          if (items.length >= maxItems) break;
        }
        if (items.length >= maxItems) break;

        const next = (res as any)?.next_cursor;
        if (next === null || next === undefined) break;
        cursor = Number(next);
        if (!Number.isFinite(cursor)) break;
      }

      return { items };
    },
    // Only do the broader fetch when explicitly enabled and there is a query.
    enabled: searchAll && Boolean(trimmedSearch),
    staleTime: 10_000,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const outcomesQuery = useQuery({
    queryKey: ['developer', 'workflowRuns', 'outcomes', selectedRunId] as const,
    queryFn: async () => {
      // Preferred: proxy to orchestrator's native GET /runs/:id/outcomes (no id_service).
      return await bff.get<any>(`/orchestrator/runs/${encodeURIComponent(selectedRunId)}/outcomes`);
    },
    staleTime: 0,
    // Only fetch for the selected row.
    enabled: Boolean(selectedRunId),
  });

  const runQuery = useQuery({
    queryKey: ['developer', 'workflowRuns', 'get', selectedRunId] as const,
    queryFn: () => getWorkflowRunApi(selectedRunId),
    staleTime: 0,
    enabled: Boolean(selectedRunId),
  });

  const executionsQuery = useQuery({
    queryKey: ['developer', 'workflowRuns', 'executions', selectedRunId] as const,
    queryFn: async () => {
      return await bff.get<any>(`/orchestrator/runs/${encodeURIComponent(selectedRunId)}/executions`);
    },
    staleTime: 0,
    enabled: Boolean(selectedRunId),
  });

  const stepsQuery = useQuery({
    queryKey: ['developer', 'workflowRuns', 'steps', selectedRunId] as const,
    queryFn: async () => {
      return await bff.get<any>(`/orchestrator/runs/${encodeURIComponent(selectedRunId)}/steps`);
    },
    staleTime: 0,
    enabled: Boolean(selectedRunId),
  });

  const runs = useMemo(() => {
    const source = (searchAll && allRunsQuery.data)
      ? (allRunsQuery.data as any)
      : (data as any);

    const items: WorkflowRunSummary[] = Array.isArray(source?.items) ? source.items : [];
    return items.slice();
  }, [allRunsQuery.data, data, searchAll]);

  const filteredRuns = useMemo(() => {
    if (!matcher) return runs;
    return runs.filter((r) => {
      const wf = pickWorkflowKey(r);
      const id = pickRunId(r);
      const st = pickStatus(r);
      return matcher(wf) || matcher(id) || matcher(st);
    });
  }, [matcher, runs]);

  // When entering the Activity Log while a run is in progress, auto-select it.
  // This mirrors the UX of clicking the row, but does not override a user's explicit selection.
  useEffect(() => {
    if (selectedRunId) {
      // If the selected run is no longer in the list, fall back to the newest row.
      const stillExists = filteredRuns.some((r) => pickRunId(r) === selectedRunId);
      if (!stillExists && filteredRuns.length > 0) setSelectedRunId(pickRunId(filteredRuns[0]) || '');
      return;
    }

    if (filteredRuns.length === 0) return;

    const running = filteredRuns.find((r) => isRunningStatus(pickStatus(r)));
    const next = pickRunId(running ?? filteredRuns[0]);
    if (next) setSelectedRunId(next);
  }, [filteredRuns, selectedRunId]);

  return (
    <div className="h-full min-h-0 rounded-md border border-neutral-800 flex flex-col">
      <div className="flex-1 min-h-0 flex">
        {/* Left: activity log */}
        <div className="min-h-0 min-w-0 flex flex-col border-r border-neutral-800" style={{ flex: 6 }}>
          <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between gap-3">
            <div className="text-sm text-neutral-200">Activity Log</div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-neutral-500 select-none">
                <input
                  type="checkbox"
                  className="accent-neutral-200"
                  checked={searchAll}
                  onChange={(e) => setSearchAll(e.target.checked)}
                />
                search in all registers
              </label>

              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search (supports *)"
                className="w-[18rem] h-8 px-2 rounded-md pac-glass text-neutral-200 placeholder:text-neutral-600 text-sm outline-none focus:ring-1 focus:ring-neutral-700"
              />

              <div className="text-xs text-neutral-500 whitespace-nowrap">
                {searchAll && trimmedSearch ? 'All registers' : 'Latest 50 runs'}
              </div>
            </div>
          </div>

          <div className="p-3 flex-1 min-h-0 overflow-auto">
          {isLoading && <div className="text-sm text-neutral-400">Loading runs…</div>}
          {error && <div className="text-sm text-rose-400">Failed to load runs: {(error as any)?.message ?? String(error)}</div>}

          {searchAll && Boolean(trimmedSearch) && allRunsQuery.isLoading && (
            <div className="text-sm text-neutral-400">Searching all registers…</div>
          )}
          {searchAll && Boolean(trimmedSearch) && allRunsQuery.error && (
            <div className="text-sm text-rose-400">Search failed: {(allRunsQuery.error as any)?.message ?? String(allRunsQuery.error)}</div>
          )}

          {!isLoading && !error && filteredRuns.length === 0 && (
            <div className="text-sm text-neutral-500">{hasSearchQuery ? 'No matches.' : 'No workflow runs yet.'}</div>
          )}

          {!isLoading && !error && filteredRuns.length > 0 && (
            <DarkDataTable<WorkflowRunSummary>
              columns={[
                {
                  header: 'Time',
                  thClassName: 'whitespace-nowrap',
                  tdClassName: 'text-neutral-400 whitespace-nowrap',
                  render: (r) => {
                    const created = pickCreatedAt(r);
                    return <div title={formatDateShort(created)}>{timeAgo(created)}</div>;
                  },
                },
                {
                  header: 'Status',
                  thClassName: 'whitespace-nowrap',
                  tdClassName: 'whitespace-nowrap',
                  render: (r) => {
                    const runId = pickRunId(r);
                    const st = pickStatus(r) || '—';
                    const isSelected = Boolean(runId) && runId === selectedRunId;
                    return <RunStatusPill runId={runId} summaryStatus={st} enabled={isSelected} />;
                  },
                },
                {
                  header: 'Workflow',
                  tdClassName: 'text-neutral-200 max-w-[22rem]',
                  render: (r) => {
                    const wf = pickWorkflowKey(r) || '—';
                    return <div className="truncate" title={wf}>{wf}</div>;
                  },
                },
                {
                  header: 'Run ID',
                  tdClassName: 'text-neutral-400 max-w-[18rem]',
                  render: (r) => {
                    const runId = pickRunId(r);
                    return <div className="truncate" title={runId || ''}>{runId || '—'}</div>;
                  },
                },
                {
                  header: 'Params',
                  tdClassName: 'max-w-[32rem]',
                  render: (r) => {
                    const runId = pickRunId(r);
                    const isSelected = Boolean(runId) && runId === selectedRunId;
                    return <RunInputs runId={runId} enabled={isSelected} />;
                  },
                },
              ]}
              rows={filteredRuns}
              emptyMessage={hasSearchQuery ? 'No matches.' : 'No workflow runs yet.'}
              getRowKey={(r) => {
                const runId = pickRunId(r);
                const wf = pickWorkflowKey(r) || '—';
                const created = pickCreatedAt(r);
                return runId || `${wf}-${created}`;
              }}
              onRowClick={(r) => setSelectedRunId(pickRunId(r))}
              isRowSelected={(r) => {
                const runId = pickRunId(r);
                return Boolean(runId) && runId === selectedRunId;
              }}
            />
          )}
          </div>
        </div>

        <WorkflowRunOutcomesPanel
          selectedRunId={selectedRunId}
          isFetching={outcomesQuery.isFetching}
          error={outcomesQuery.error}
          data={outcomesQuery.data}
          runIsFetching={runQuery.isFetching}
          runError={runQuery.error}
          runData={runQuery.data}
          stepsIsFetching={stepsQuery.isFetching}
          stepsError={stepsQuery.error}
          stepsData={stepsQuery.data}
          executionsIsFetching={executionsQuery.isFetching}
          executionsError={executionsQuery.error}
          executionsData={executionsQuery.data}
        />
      </div>
    </div>
  );
}
